<template>
  <div class="map" ref="map"></div>
</template>

<script>
  import axios from 'axios'
  import L from '@/plugins/leaflet'

  import 'leaflet/dist/leaflet.css'

  import { leafletIndexeddbStore, localstorageStore } from '@/plugins/localforage'

  import MapPopup from './MapPopup.vue'
  import Vue from 'vue'
  import router from '@/router/index'
  import store from '@/store/index'
  import i18n from '@/plugins/i18n'

  const MapPopupClass = Vue.extend(MapPopup)

  export default {
    name: 'MapContainer',

    props: {
      pois: {
        required: true,
      },

      idOnHover: {
        default: '',
      },
    },

    data() {
      return {
        map: null,
        layer: null,
        markersGroup: null,
        parsedPois: [],
        tilesAreSaved: false,
        MapPopupClass,
      }
    },

    watch: {
      pois(pois) {
        this.parsedPois = this.parsePois(pois)
        this.addMarkers()
      },

      idOnHover(id) {
        if (!id) return this.map.closePopup()

        const poi = this.parsedPois.find((poi) => poi.poi.id === id)

        if (!poi) return

        const popupContent = this.createPopupContent(poi.poi)
        const position = L.latLng(poi.position[0], poi.position[1])

        return L.popup()
          .setLatLng(position)
          .setContent(popupContent)
          .openOn(this.map)
      },
    },

    computed: {
      locale() {
        return this.$store.state.locale
      },

      isOnline() {
        return this.$store.state.isOnline
      },

      offlineMapZoom() {
        return this.$store.state.offlineMapZoom
      },

      mapOptions() {
        const options = {
          center: [46.547938, 6.620846],
          zoom: 9,
          dragging: this.isOnline,
        }

        if (!this.isOnline) {
          const mapZoom = this.offlineMapZoom || 12
          options.zoom = mapZoom
          options.minZoom = mapZoom
          options.maxZoom = mapZoom
        }

        return options
      },
    },

    methods: {
      async saveTiles() {
        const zoom = this.map.getZoom()
        const bounds = L.bounds(
          this.map.project(this.map.getBounds().getNorthWest(), zoom),
          this.map.project(this.map.getBounds().getSouthEast(), zoom),
        )

        await localstorageStore.setItem('mapZoom', zoom)

        const tiles = this.layer.getTileUrls(bounds, zoom)

        tiles.forEach((tile) => this.saveTile(tile))

        this.tilesAreSaved = true
      },

      async saveTile(tile) {
        const response = await axios({
          method: 'GET',
          url: tile.url,
          responseType: 'arraybuffer',
        })

        const buffer = Buffer.from(response.data, 'binary')
        const blob = new Blob([buffer], { type: 'image/png' })

        await leafletIndexeddbStore.setItem(tile.key, blob)
      },

      parsePois() {
        const parsedPois = this.pois
          .filter((poi) => poi.geolocations.length > 0)
          .map((poi) => {
            const geolocation = poi.geolocations[0]

            return {
              poi,
              position: [
                Number(geolocation.latitude),
                Number(geolocation.longitude),
              ],
            }
          })

        return parsedPois
      },

      addMarkers() {
        const markers = []

        if (this.markersGroup) this.map.removeLayer(this.markersGroup)

        const icon = L.icon({
          iconUrl: '/map/pin.svg',
          iconSize: [35, 35],
        })

        this.parsedPois.forEach((poi) => {
          const marker = L.marker(poi.position, { icon })

          marker.on('mouseover', (e) => e.target.openPopup())

          const popupContent = this.createPopupContent(poi.poi)
          const popup = L.popup().setContent(popupContent)

          marker.bindPopup(popup)
          markers.push(marker)
        })

        const featureGroup = L.featureGroup(markers).addTo(this.map)
        this.markersGroup = featureGroup
        const bounds = featureGroup.getBounds()

        if (Object.keys(bounds).length > 0) {
          this.map.fitBounds(bounds)
          this.map.on('zoomend', () => {
            if (this.isOnline && !this.tilesAreSaved) this.saveTiles()
          })
        }
      },

      createPopupContent(poi) {
        const MapPopupInstance = new MapPopupClass({
          router,
          store,
          i18n,
          propsData: {
            poi,
            showLink: this.isOnline,
          },
        }).$mount()

        return MapPopupInstance.$el.outerHTML
      },
    },

    mounted() {
      const mapElement = this.$refs.map

      this.map = L.map(mapElement, this.mapOptions)

      const layer = L.tileLayer.offline('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors',
      })

      layer.addTo(this.map)
      this.layer = layer

      this.parsedPois = this.parsePois(this.pois)
      this.addMarkers()
    },
  }
</script>

<style lang="sass" scoped>
  @import "@/assets/styles/_config.sass"
  @import "@/assets/styles/_mixins.sass"

  .map
    z-index: 1
    height: 50vh

  ::v-deep
    .leaflet-popup-content-wrapper
      border-radius: 0 !important

    .v-image.v-responsive.theme--light
      height: 100%

    .leaflet-popup-content
      margin: 0px
      width: 100% !important

      &-wrapper
        border-radius: 0 !important

    .map-popup
      &__container
        min-width: 400px

      &__title
        font-size: 18px

      &__text-container
        padding: 15px 10px 0 10px

        p
          margin-bottom: 10px

      &__link
        color: $primary-color
        text-decoration: none
        margin-bottom: 10px

  @media (max-width: 960px)
    .map
      height: 50vh

    .map-popup__container
      min-width: 300px
</style>
